// Standard CRA PWA-template registration helper, trimmed to what this app
// needs. Registering a service worker (even one that only precaches the
// static shell — see src/service-worker.ts) is a Chrome installability
// requirement: without it, `beforeinstallprompt` never fires and the
// "Add to Home Screen" affordances on Login.tsx / InstallPrompt.tsx are
// permanently inert. Deliberately does NOT intercept API calls — see
// service-worker.ts for the route scope (static shell + images only).

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// How often an already-open tab asks whether a new build has shipped.
const UPDATE_CHECK_MS = 60 * 60 * 1000;

export function register() {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;

  const publicUrl = new URL(process.env.PUBLIC_URL || "", window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener("load", () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl);
    } else {
      registerValidSW(swUrl);
    }
  });
}

function registerValidSW(swUrl: string) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // A new build's worker installs and WAITS (see service-worker.ts). Each
      // tab then does what any web app does:
      //
      //  - A tab that is already the new build (opened or reloaded after the
      //    deploy; navigations are network-first) activates it silently. No
      //    prompt, nothing reloads.
      //  - A tab still running the old build keeps working untouched and shows
      //    a "new version" pill. Tapping Refresh activates the new worker and
      //    reloads once it takes over.
      //
      // Never reload on the user's behalf. Both automatic variants failed:
      // reloading while hidden left iOS PWAs resuming to a white page
      // (b496088e), and reloading on return to visible reloaded every tab you
      // switched back to, since dev deploys several times a day.
      // `controller` is null on the very first install: no older build to
      // replace, nothing to offer.
      let refreshing = false;
      let offered = false;
      const handleWaiting = (worker: ServiceWorker) => {
        if (!navigator.serviceWorker.controller) return;
        isThisBuild(worker).then((same) => {
          if (same) {
            worker.postMessage({ type: "SKIP_WAITING" });
          } else if (!offered) {
            offered = true;
            showUpdatePill(() => {
              // Another tab may have activated it already; then the worker is
              // no longer waiting and a plain reload picks up the new build.
              const waiting = registration.waiting;
              if (!waiting) { window.location.reload(); return; }
              refreshing = true;
              waiting.postMessage({ type: "SKIP_WAITING" });
            });
          }
        });
      };

      if (registration.waiting) handleWaiting(registration.waiting);
      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed") handleWaiting(installing);
        });
      });

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        // Only the tab whose user tapped Refresh reloads. controllerchange
        // can repeat, so guard against a refresh loop.
        if (!refreshing) return;
        refreshing = false;
        window.location.reload();
      });

      // A tab open across a deploy never re-runs registration, so it would
      // sit on the old build indefinitely. Ask periodically; update() is a
      // conditional request and a no-op when nothing changed.
      setInterval(() => {
        registration.update().catch(() => {
          /* offline or transient — the next tick retries */
        });
      }, UPDATE_CHECK_MS);
    })
    .catch((error) => {
      console.error("Service worker registration failed:", error);
    });
}

// Plain DOM, not React: this runs outside the app tree and has to work on
// every screen, signed in or not. Sits above the mobile bottom nav.
// Is this page running the same build as `worker`? Bundle filenames are
// content-hashed, so the worker's index.html names the page's main script
// only when they match. Any doubt counts as "different": the cost of
// a wrong "different" is one unneeded pill, the cost of a wrong "same" is an
// old tab losing its files.
function isThisBuild(worker: ServiceWorker): Promise<boolean> {
  const main = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]');
  if (!main) return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(false), 3000);
    channel.port1.onmessage = (e) => { window.clearTimeout(timer); resolve(e.data === true); };
    worker.postMessage({ type: "SAME_BUILD", path: new URL(main.src).pathname }, [channel.port2]);
  });
}

function showUpdatePill(onRefresh: () => void) {
  const pill = document.createElement("div");
  pill.setAttribute("role", "status");
  pill.style.cssText = [
    "position:fixed", "left:50%", "transform:translateX(-50%)",
    "bottom:calc(env(safe-area-inset-bottom, 0px) + 84px)", "z-index:2147483000",
    "display:flex", "align-items:center", "gap:12px", "padding:8px 8px 8px 14px",
    "background:#111827", "color:#fff", "border-radius:4px",
    "box-shadow:0 4px 16px rgba(0,0,0,0.18)", "font:500 13px 'DM Sans',system-ui,sans-serif",
    "white-space:nowrap",
  ].join(";");
  const label = document.createElement("span");
  label.textContent = "A new version is ready.";
  const refresh = document.createElement("button");
  refresh.type = "button";
  refresh.textContent = "Refresh";
  refresh.style.cssText = "background:#5A9E3A;color:#fff;border:none;border-radius:4px;padding:6px 12px;font:600 13px 'DM Sans',system-ui,sans-serif;cursor:pointer";
  refresh.onclick = () => { refresh.disabled = true; onRefresh(); };
  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.setAttribute("aria-label", "Dismiss");
  dismiss.textContent = "\u00d7";
  dismiss.style.cssText = "background:transparent;color:#9CA3AF;border:none;font-size:18px;line-height:1;padding:4px 6px;cursor:pointer";
  dismiss.onclick = () => pill.remove();
  pill.append(label, refresh, dismiss);
  document.body.appendChild(pill);
}

function checkValidServiceWorker(swUrl: string) {
  fetch(swUrl, { headers: { "Service-Worker": "script" } })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister();
        });
      } else {
        registerValidSW(swUrl);
      }
    })
    .catch(() => {
      // offline — the app still works, just without a cached shell
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}
